import { parseDurations } from './parseDurations';

/**
 * Cook mode summary card — pulls the "set this up before you start" facts out of a
 * recipe's free-text steps: oven/grill temperatures, stovetop heat levels, the cooking
 * method(s), the pan or appliance, and how much timed cooking the steps add up to.
 *
 * Recipes carry no structured temperature/method fields (steps are plain strings), so this
 * reads the text. Like `parseDurations` it is deliberately narrow — a fact appears only when
 * the grammar clearly produces it ("350°F", "425 F", "200 degrees C", "medium-high heat",
 * "air fry"), and a miss just means one fewer line on the card, never a wrong instruction.
 * Every fact remembers the step it came from so the card can jump there.
 */

export type TemperatureKind = 'oven' | 'grill' | 'oil' | 'internal' | 'other';

export interface ParsedTemperature {
  /** Lower bound of a range ("375–400°F" -> 375). */
  value: number;
  /** Upper bound when the text gave a range. */
  high?: number;
  unit: 'F' | 'C';
  kind: TemperatureKind;
  stepIndex: number;
}

export interface ParsedFact {
  label: string;
  stepIndex: number;
}

export interface CookSettings {
  temperatures: ParsedTemperature[];
  /** "Medium-high heat", in order of first mention. */
  heat: ParsedFact[];
  /** "Bake", "Sauté", ... in order of first mention. */
  methods: ParsedFact[];
  /** The single method that best names the dish's cooking (see `METHOD_PRIORITY`). */
  primaryMethod: ParsedFact | null;
  /** "Dutch oven", "Sheet pan", ... */
  equipment: ParsedFact[];
  /** Sum of every step duration's lower bound, in seconds (0 when the steps give none). */
  timedSeconds: number;
}

// ---- temperatures --------------------------------------------------------------------

/**
 * A 2–3 digit number (optionally a range) followed by one of: a degree sign with an
 * optional F/C, the word "degrees" with an optional F/C/Fahrenheit/Celsius, or a bare F/C
 * letter. The bare-letter form is the one that could collide with "2 C flour" (cups), so it
 * alone needs a three-digit value — see `plausible`.
 */
const TEMP_RE =
  /(\d{2,3})(?:\s*(?:-|–|to)\s*(\d{2,3}))?\s*(?:([°º˚])\s*([FC])?|degrees?\s*(?:([FC]|fahrenheit|celsius)\b)?|([FC])\b)/gi;

const BARE_OVEN_RE =
  /\b(?:at|to)\s+(\d{3})\b(?!\s*(?:[-–]|to\b|[°º˚]|degrees?|(?:g|kg|ml|l|grams?|oz|lbs?|cups?|mm|cm|w|watts?)\b))/gi;

function plausible(value: number, unit: 'F' | 'C', bareLetter: boolean): boolean {
  if (bareLetter && value < 100) return false;
  return unit === 'F' ? value >= 90 && value <= 700 : value >= 30 && value <= 370;
}

function classifyTemperature(sentence: string): TemperatureKind {
  const s = sentence.toLowerCase();
  if (/\binternal|thermometer|registers|reads\b|instant[- ]read/.test(s)) return 'internal';
  if (/\bgrill|smoker|coals\b/.test(s)) return 'grill';
  if (/\boil\b/.test(s) && /\bfry|fryer|heat\b/.test(s) && !/\boven\b/.test(s)) return 'oil';
  if (/\boven|preheat|bake|roast|broil|air[- ]?fry/.test(s)) return 'oven';
  return 'other';
}

/** The sentence around a match, so "Preheat the oven to 350°F. Heat oil to 375°F." classifies
 * each temperature by its own sentence rather than the whole step. */
function sentenceAround(text: string, start: number, end: number): string {
  const before = text.lastIndexOf('.', start - 1);
  const afterDot = text.indexOf('.', end);
  return text.slice(before + 1, afterDot === -1 ? text.length : afterDot);
}

/**
 * "Bake 35 minutes, until the thighs reach 165°F" — the sentence says bake, but the number
 * is a doneness target, not an oven setting. The words just before the number decide it.
 */
const DONENESS_LEAD_RE = /\b(?:until|reach(?:es|ed)?|registers?|done at|cooked to)\b[^.]*$/i;

function isDonenessTarget(text: string, start: number): boolean {
  const sentenceStart = text.lastIndexOf('.', start - 1) + 1;
  return DONENESS_LEAD_RE.test(text.slice(Math.max(sentenceStart, start - 48), start));
}

export function parseTemperatures(text: string, stepIndex = 0): ParsedTemperature[] {
  const out: ParsedTemperature[] = [];
  const covered: Array<[number, number]> = [];
  TEMP_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TEMP_RE.exec(text)) !== null) {
    const [raw, lowRaw, highRaw, , degreeLetter, wordUnit, bareLetter] = m;
    const letter = (degreeLetter ?? wordUnit ?? bareLetter ?? '').toUpperCase();
    // "350 degrees" / "350°" with no unit: US recipes mean Fahrenheit.
    const unit: 'F' | 'C' = letter.startsWith('C') ? 'C' : 'F';
    const value = Number(lowRaw);
    if (!plausible(value, unit, Boolean(bareLetter))) continue;
    const high = highRaw ? Number(highRaw) : undefined;
    const sentence = sentenceAround(text, m.index, m.index + raw.length);
    const sentenceKind = classifyTemperature(sentence);
    // "Heat the oil until it reaches 350°F" is still a setting, so only a sentence that
    // isn't heating the appliance/oil itself can turn a number into a doneness target.
    const kind =
      sentenceKind !== 'oil' &&
      !/\b(?:pre)?heat\b/i.test(sentence) &&
      isDonenessTarget(text, m.index)
        ? 'internal'
        : sentenceKind;
    out.push({ value, ...(high && high > value ? { high } : {}), unit, kind, stepIndex });
    covered.push([m.index, m.index + raw.length]);
  }

  // "Bake at 375 for 20 minutes" — no unit at all. Only trusted for a three-digit number
  // right after "at"/"to" in a sentence that is plainly about the oven or grill, and never
  // when a unit that isn't a temperature follows ("to 350 g").
  BARE_OVEN_RE.lastIndex = 0;
  while ((m = BARE_OVEN_RE.exec(text)) !== null) {
    const start = m.index + m[0].length - m[1]!.length;
    if (covered.some(([a, b]) => start >= a && start < b)) continue;
    const value = Number(m[1]);
    const kind = classifyTemperature(sentenceAround(text, start, start + m[1]!.length));
    if ((kind !== 'oven' && kind !== 'grill') || !plausible(value, 'F', false)) continue;
    out.push({ value, unit: 'F', kind, stepIndex });
  }
  return out;
}

/** "350°F", "375–400°F", or converted for a metric/US units preference ("175°C"). Oven
 * conversions round to the nearest 5, which is how ovens and recipes actually talk. */
export function formatTemperature(
  t: Pick<ParsedTemperature, 'value' | 'high' | 'unit'>,
  target: 'F' | 'C' | null = null,
): string {
  const to = target ?? t.unit;
  const convert = (v: number): number => {
    if (to === t.unit) return v;
    const exact = to === 'C' ? ((v - 32) * 5) / 9 : (v * 9) / 5 + 32;
    return Math.round(exact / 5) * 5;
  };
  const low = convert(t.value);
  const high = t.high !== undefined ? convert(t.high) : undefined;
  return high !== undefined ? `${low}–${high}°${to}` : `${low}°${to}`;
}

// ---- heat, methods, equipment --------------------------------------------------------

const HEAT_RE = /\b(low|medium[- ]low|medium|medium[- ]high|high)\s+(?:heat|flame)\b/gi;

function normalizeHeat(level: string): string {
  const l = level.toLowerCase().replace(/\s+/, '-');
  return `${l.charAt(0).toUpperCase()}${l.slice(1)} heat`;
}

/** Order here is priority for `primaryMethod`: the more specific, dish-defining method wins
 * (a recipe that sautés onions and then bakes for an hour is a bake). */
const METHODS: Array<{ label: string; re: RegExp }> = [
  { label: 'Air fry', re: /\bair[- ]?fr(?:y|ies|ied|ying|yer)\b/i },
  { label: 'Pressure cook', re: /\bpressure[- ]cook|\binstant pot\b|\bpressure cooker\b/i },
  { label: 'Slow cook', re: /\bslow[- ]cook|\bslow cooker\b|\bcrock[- ]?pot\b/i },
  { label: 'Smoke', re: /\bsmok(?:e|es|ed|ing)\b(?!\s+point)|\bsmoker\b/i },
  { label: 'Grill', re: /\bgrill(?:s|ed|ing)?\b(?!\s+pan)/i },
  { label: 'Deep fry', re: /\bdeep[- ]fr(?:y|ies|ied|ying)\b/i },
  { label: 'Bake', re: /\bbak(?:e|es|ed|ing)\b(?!\s+(?:sheet|dish|pan))/i },
  { label: 'Roast', re: /\broast(?:s|ed|ing)?\b(?!\s+pan)/i },
  { label: 'Broil', re: /\bbroil(?:s|ed|ing)?\b/i },
  { label: 'Braise', re: /\bbrais(?:e|es|ed|ing)\b/i },
  { label: 'Stir-fry', re: /\bstir[- ]fr(?:y|ies|ied|ying)\b/i },
  { label: 'Pan fry', re: /\bpan[- ]fr(?:y|ies|ied|ying)\b/i },
  { label: 'Fry', re: /(?<!\b(?:air|deep|stir|pan)[- ]?)\bfr(?:y|ies|ied|ying)\b/i },
  { label: 'Sear', re: /\bsear(?:s|ed|ing)?\b/i },
  { label: 'Sauté', re: /\bsaut[eé](?:e?d|s|ing)?(?![a-z])/i },
  { label: 'Steam', re: /\bsteam(?:s|ed|ing)?\b/i },
  { label: 'Simmer', re: /\bsimmer(?:s|ed|ing)?\b/i },
  { label: 'Boil', re: /\bboil(?:s|ed|ing)?\b/i },
];

const EQUIPMENT: Array<{ label: string; re: RegExp }> = [
  { label: 'Dutch oven', re: /\bdutch oven\b/i },
  { label: 'Cast-iron skillet', re: /\bcast[- ]iron\b/i },
  { label: 'Skillet', re: /\bskillet\b|\bfrying pan\b/i },
  { label: 'Sheet pan', re: /\bsheet pan\b|\bbaking sheet\b|\bcookie sheet\b/i },
  { label: 'Baking dish', re: /\bbaking dish\b|\bcasserole dish\b|\b9\s*[x×]\s*13\b/i },
  { label: 'Loaf pan', re: /\bloaf pan\b/i },
  { label: 'Muffin tin', re: /\bmuffin (?:tin|pan)\b|\bcupcake pan\b/i },
  { label: 'Cake pan', re: /\bcake pan\b|\bspringform\b/i },
  { label: 'Pie dish', re: /\bpie (?:dish|plate|pan)\b/i },
  { label: 'Roasting pan', re: /\broasting pan\b/i },
  { label: 'Grill pan', re: /\bgrill pan\b/i },
  { label: 'Wok', re: /\bwok\b/i },
  { label: 'Saucepan', re: /\bsauce ?pan\b/i },
  { label: 'Stockpot', re: /\bstock ?pot\b|\blarge pot\b/i },
  { label: 'Slow cooker', re: /\bslow cooker\b|\bcrock[- ]?pot\b/i },
  { label: 'Instant Pot', re: /\binstant pot\b|\bpressure cooker\b/i },
  { label: 'Air fryer', re: /\bair fryer\b/i },
];

function pushUnique(list: ParsedFact[], label: string, stepIndex: number): void {
  if (!list.some((f) => f.label === label)) list.push({ label, stepIndex });
}

// ---- whole recipe --------------------------------------------------------------------

export function parseCookSettings(steps: readonly string[]): CookSettings {
  const temperatures: ParsedTemperature[] = [];
  const heat: ParsedFact[] = [];
  const methods: ParsedFact[] = [];
  const equipment: ParsedFact[] = [];
  let timedSeconds = 0;

  steps.forEach((step, i) => {
    for (const t of parseTemperatures(step, i)) {
      const dup = temperatures.some(
        (x) => x.value === t.value && x.unit === t.unit && x.kind === t.kind,
      );
      if (!dup) temperatures.push(t);
    }
    HEAT_RE.lastIndex = 0;
    let h: RegExpExecArray | null;
    while ((h = HEAT_RE.exec(step)) !== null) pushUnique(heat, normalizeHeat(h[1]!), i);
    for (const { label, re } of METHODS) if (re.test(step)) pushUnique(methods, label, i);
    for (const { label, re } of EQUIPMENT) if (re.test(step)) pushUnique(equipment, label, i);
    for (const d of parseDurations(step)) timedSeconds += d.seconds;
  });

  // "Cast-iron skillet" already says skillet.
  const redundant: Record<string, string> = { Skillet: 'Cast-iron skillet' };
  const equipmentOut = equipment.filter(
    (e) => !redundant[e.label] || !equipment.some((x) => x.label === redundant[e.label]),
  );

  // Equipment already implies its method's appliance ("Air fryer" + "Air fry") — keep both;
  // they answer different questions (what to grab vs. how it cooks).
  const order = METHODS.map((m) => m.label);
  const primaryMethod =
    [...methods].sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label))[0] ?? null;

  return { temperatures, heat, methods, primaryMethod, equipment: equipmentOut, timedSeconds };
}

/** "45 min", "1 hr 10 min", "2 hrs" — the card's timed-cooking line. */
export function formatTotalTime(seconds: number): string {
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  const hourPart = `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  return minutes === 0 ? hourPart : `${hourPart} ${minutes} min`;
}
